import React, { useState, useEffect } from 'react';

const PrivacyPolicy = ({ onNavigate }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const sectionStyle = {
    marginBottom: '2rem'
  };

  const titleStyle = {
    fontSize: isMobile ? '1.8rem' : '2.5rem',
    color: '#2c3e50',
    textAlign: 'center',
    marginBottom: '0.5rem',
    fontWeight: 'bold'
  };

  const appNameStyle = {
    fontSize: isMobile ? '1.2rem' : '1.5rem',
    color: '#3498db',
    textAlign: 'center',
    fontWeight: '600',
    marginBottom: '0.25rem'
  };

  const lastUpdatedStyle = {
    fontSize: isMobile ? '0.85rem' : '0.9rem',
    color: '#7f8c8d',
    fontStyle: 'italic',
    textAlign: 'center',
    marginBottom: '2rem'
  };

  const sectionTitleStyle = {
    fontSize: isMobile ? '1.3rem' : '1.5rem',
    color: '#34495e',
    marginBottom: '1rem',
    fontWeight: 'bold',
    borderBottom: '2px solid #3498db',
    paddingBottom: '0.5rem'
  };

  const subTitleStyle = {
    fontSize: isMobile ? '1.1rem' : '1.2rem',
    color: '#555',
    marginTop: '1.25rem',
    marginBottom: '0.75rem',
    fontWeight: '600'
  };

  const paragraphStyle = {
    fontSize: isMobile ? '0.95rem' : '1rem',
    color: '#333',
    lineHeight: '1.6',
    marginBottom: '0.75rem',
    textAlign: 'justify'
  };

  const bulletPointStyle = {
    fontSize: isMobile ? '0.95rem' : '1rem',
    color: '#333',
    lineHeight: '1.6',
    marginLeft: '1.5rem',
    marginBottom: '0.5rem'
  };

  const contactInfoStyle = {
    fontSize: isMobile ? '0.95rem' : '1rem',
    color: '#2c3e50',
    lineHeight: '1.6',
    marginBottom: '0.5rem',
    fontWeight: '500'
  };

  const importantBoxStyle = {
    backgroundColor: '#fff3cd',
    borderLeft: '4px solid #ffc107',
    padding: '1rem',
    marginTop: '1rem',
    marginBottom: '1rem',
    borderRadius: '4px'
  };

  const contactBoxStyle = {
    backgroundColor: '#ecf0f1',
    padding: isMobile ? '1.25rem' : '1.5rem',
    borderRadius: '8px',
    marginTop: '1rem'
  };

  const footerStyle = {
    marginTop: '2.5rem',
    paddingTop: '1.5rem',
    borderTop: '2px solid #e0e0e0',
    textAlign: 'center'
  };

  const footerTextStyle = {
    fontSize: isMobile ? '0.8rem' : '0.85rem',
    color: '#7f8c8d',
    marginBottom: '0.5rem'
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: isMobile ? '1rem' : '2rem'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        backgroundColor: 'white',
        padding: isMobile ? '1.5rem' : '2.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <h1 style={titleStyle}>Privacy Policy</h1>
        <div style={appNameStyle}>SRIMS Hostels App</div>
        <div style={lastUpdatedStyle}>Last Updated: December 18, 2024</div>

        {/* Section 1: Introduction */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>1. Introduction</h2>
          <p style={paragraphStyle}>
            Welcome to SRIMS Hostels, a comprehensive hostel and food management application developed by Learning Hub. 
            This application is designed exclusively for students of Shri Ram Institute of Management Studies (SRIMS). 
            We are committed to protecting your privacy and ensuring the security of your personal information.
          </p>
          <p style={paragraphStyle}>
            By using the SRIMS Hostels app, you agree to the collection and use of information in accordance with this policy.
          </p>
        </div>

        {/* Section 2: Eligibility */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>2. Eligibility</h2>
          <p style={paragraphStyle}>
            This application is exclusively available to:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Current students of Shri Ram Institute of Management Studies (SRIMS)</li>
            <li style={bulletPointStyle}>Authorized hostel staff and administrators</li>
            <li style={bulletPointStyle}>Faculty members with appropriate permissions</li>
          </ul>
          <p style={paragraphStyle}>
            Users must provide valid SRIMS credentials and be verified by the institution to access the app's features.
          </p>
        </div>

        {/* Section 3: Information We Collect */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>3. Information We Collect</h2>
          
          <h3 style={subTitleStyle}>3.1 Personal Information</h3>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Full name and student ID</li>
            <li style={bulletPointStyle}>Email address and phone number</li>
            <li style={bulletPointStyle}>Hostel room number and block details</li>
            <li style={bulletPointStyle}>Course and academic year information</li>
            <li style={bulletPointStyle}>Emergency contact details</li>
          </ul>

          <h3 style={subTitleStyle}>3.2 Biometric Data</h3>
          <ul style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>
            <li style={bulletPointStyle}>Facial recognition data for hostel entry/exit tracking</li>
            <li style={bulletPointStyle}>Fingerprint data for authentication (if enabled)</li>
          </ul>
          <div style={importantBoxStyle}>
            <strong>Note:</strong> Biometric data is encrypted and stored securely on our servers. It is used solely for identity 
            verification and attendance tracking purposes.
          </div>

          <h3 style={subTitleStyle}>3.3 Usage Data</h3>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Hostel entry and exit times</li>
            <li style={bulletPointStyle}>Food/meal preferences and consumption patterns</li>
            <li style={bulletPointStyle}>Complaint and feedback submissions</li>
            <li style={bulletPointStyle}>Device information and app usage statistics</li>
            <li style={bulletPointStyle}>Network access logs and timestamps</li>
          </ul>

          <h3 style={subTitleStyle}>3.4 Photos and Camera Access</h3>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Profile photos uploaded by users</li>
            <li style={bulletPointStyle}>Photos for complaint/maintenance requests</li>
            <li style={bulletPointStyle}>Real-time camera access for facial recognition at hostel gates</li>
          </ul>

          <h3 style={subTitleStyle}>3.5 Storage Access</h3>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Read/write access to device storage for saving documents</li>
            <li style={bulletPointStyle}>Storing meal receipts and payment records</li>
            <li style={bulletPointStyle}>Caching app data for offline functionality</li>
          </ul>
        </div>

        {/* Section 4: How We Use Your Information */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>4. How We Use Your Information</h2>
          <p style={paragraphStyle}>
            We use the collected information for the following purposes:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Managing hostel room allocations and occupancy</li>
            <li style={bulletPointStyle}>Tracking student entry/exit for security purposes</li>
            <li style={bulletPointStyle}>Managing food services, meal plans, and dietary preferences</li>
            <li style={bulletPointStyle}>Processing complaints and maintenance requests</li>
            <li style={bulletPointStyle}>Sending important notifications and announcements</li>
            <li style={bulletPointStyle}>Generating attendance and usage reports</li>
            <li style={bulletPointStyle}>Ensuring campus safety and security</li>
            <li style={bulletPointStyle}>Improving app functionality and user experience</li>
            <li style={bulletPointStyle}>Contacting you for emergency situations</li>
          </ul>
        </div>

        {/* Section 5: App Permissions Explained */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>5. App Permissions Explained</h2>
          
          <h3 style={subTitleStyle}>5.1 Camera Permission</h3>
          <p style={paragraphStyle}>
            Required for facial recognition at hostel gates, profile photo capture, and documenting 
            maintenance/complaint issues.
          </p>

          <h3 style={subTitleStyle}>5.2 Storage Permission (Read & Write)</h3>
          <p style={paragraphStyle}>
            Required to save meal receipts, download documents, store profile photos, and cache data 
            for offline access.
          </p>

          <h3 style={subTitleStyle}>5.3 Biometric/Fingerprint Permission</h3>
          <p style={paragraphStyle}>
            Used for secure authentication and quick login without entering passwords repeatedly.
          </p>

          <h3 style={subTitleStyle}>5.4 Internet Access</h3>
          <p style={paragraphStyle}>
            Required to sync data with servers, receive real-time notifications, and access online features.
          </p>

          <h3 style={subTitleStyle}>5.5 Network State Access</h3>
          <p style={paragraphStyle}>
            Used to detect internet connectivity and optimize data synchronization.
          </p>

          <h3 style={subTitleStyle}>5.6 Vibration Permission</h3>
          <p style={paragraphStyle}>
            Used to provide haptic feedback for notifications and alerts.
          </p>

          <h3 style={subTitleStyle}>5.7 Audio Recording Permission</h3>
          <p style={paragraphStyle}>
            Used for voice-based complaints or feedback submissions (if this feature is enabled).
          </p>

          <h3 style={subTitleStyle}>5.8 System Alert Window</h3>
          <p style={paragraphStyle}>
            Used to display important notifications and alerts even when the app is in the background.
          </p>
        </div>

        {/* Section 6: Data Sharing and Disclosure */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>6. Data Sharing and Disclosure</h2>
          <p style={paragraphStyle}>
            We do not sell, trade, or rent your personal information to third parties. Your information 
            may be shared only in the following circumstances:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>With SRIMS administration for academic and hostel management purposes</li>
            <li style={bulletPointStyle}>With hostel wardens and security personnel for safety and discipline</li>
            <li style={bulletPointStyle}>With food service providers for meal management</li>
            <li style={bulletPointStyle}>With parents/guardians in case of emergencies</li>
            <li style={bulletPointStyle}>When required by law or legal proceedings</li>
            <li style={bulletPointStyle}>With your explicit consent</li>
          </ul>
        </div>

        {/* Section 7: Data Security */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>7. Data Security</h2>
          <p style={paragraphStyle}>
            We implement industry-standard security measures to protect your data:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>End-to-end encryption for sensitive data transmission</li>
            <li style={bulletPointStyle}>Secure servers with regular security audits</li>
            <li style={bulletPointStyle}>Encrypted storage for biometric data</li>
            <li style={bulletPointStyle}>Regular security patches and updates</li>
            <li style={bulletPointStyle}>Access controls and authentication mechanisms</li>
            <li style={bulletPointStyle}>Regular data backups to prevent data loss</li>
          </ul>
        </div>

        {/* Section 8: Data Retention */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>8. Data Retention</h2>
          <p style={paragraphStyle}>
            We retain your personal information for as long as you are a student at SRIMS and have an 
            active account. Upon graduation or account deletion:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Personal data will be archived for institutional records</li>
            <li style={bulletPointStyle}>Biometric data will be permanently deleted within 30 days</li>
            <li style={bulletPointStyle}>Usage logs may be retained for up to 1 year for security purposes</li>
            <li style={bulletPointStyle}>You can request complete data deletion by contacting us</li>
          </ul>
        </div>

        {/* Section 9: Your Rights */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>9. Your Rights</h2>
          <p style={paragraphStyle}>
            As a user, you have the following rights:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Right to access your personal data</li>
            <li style={bulletPointStyle}>Right to correct inaccurate information</li>
            <li style={bulletPointStyle}>Right to request data deletion (subject to institutional policies)</li>
            <li style={bulletPointStyle}>Right to opt-out of non-essential communications</li>
            <li style={bulletPointStyle}>Right to withdraw consent for biometric data collection</li>
            <li style={bulletPointStyle}>Right to file a complaint regarding data handling</li>
          </ul>
        </div>

        {/* Section 10: Food Management Privacy */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>10. Food Management Privacy</h2>
          <p style={paragraphStyle}>
            For the food management features:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Meal preferences are stored securely and not shared externally</li>
            <li style={bulletPointStyle}>Dietary restrictions are shared only with food service providers</li>
            <li style={bulletPointStyle}>Food consumption data is used only for meal planning and analytics</li>
            <li style={bulletPointStyle}>Payment information is processed through secure payment gateways</li>
          </ul>
        </div>

        {/* Section 11: Cookies and Tracking */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>11. Cookies and Tracking</h2>
          <p style={paragraphStyle}>
            The app may use local storage and cookies to:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '1rem' }}>
            <li style={bulletPointStyle}>Remember your login session</li>
            <li style={bulletPointStyle}>Store user preferences</li>
            <li style={bulletPointStyle}>Improve app performance</li>
            <li style={bulletPointStyle}>Analyze usage patterns</li>
          </ul>
        </div>

        {/* Section 12: Third-Party Services */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>12. Third-Party Services</h2>
          <p style={paragraphStyle}>
            The app may integrate with third-party services:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>
            <li style={bulletPointStyle}>WhatsApp API (Interakt) for notifications</li>
            <li style={bulletPointStyle}>Cloud storage services for data backup</li>
            <li style={bulletPointStyle}>Analytics tools for app performance monitoring</li>
          </ul>
          <p style={paragraphStyle}>
            These services have their own privacy policies, and we encourage you to review them.
          </p>
        </div>

        {/* Section 13: Children's Privacy */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>13. Children's Privacy</h2>
          <p style={paragraphStyle}>
            This app is intended for college students aged 18 and above. We do not knowingly collect 
            information from individuals under 18 without parental consent. If you are under 18, 
            please use this app with parental supervision.
          </p>
        </div>

        {/* Section 14: Changes to Privacy Policy */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>14. Changes to Privacy Policy</h2>
          <p style={paragraphStyle}>
            We may update this privacy policy from time to time. Users will be notified of significant 
            changes through:
          </p>
          <ul style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>
            <li style={bulletPointStyle}>In-app notifications</li>
            <li style={bulletPointStyle}>Email announcements</li>
            <li style={bulletPointStyle}>Updates on the app's settings page</li>
          </ul>
          <p style={paragraphStyle}>
            Continued use of the app after changes constitutes acceptance of the updated policy.
          </p>
        </div>

        {/* Section 15: Contact Information */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>15. Contact Information</h2>
          <p style={paragraphStyle}>
            If you have questions or concerns about this privacy policy or your data:
          </p>
          <div style={contactBoxStyle}>
            <p style={contactInfoStyle}><strong>Developed by:</strong> Learning Hub</p>
            <p style={contactInfoStyle}><strong>Email:</strong> support@learninghub.ind.in</p>
            <p style={contactInfoStyle}><strong>Website:</strong> www.learninghub.ind.in</p>
            <p style={contactInfoStyle}><strong>Address:</strong> 4c, Auckland Pl, near Jasal House, Elgin, Kolkata, West Bengal 700017</p>
            <p style={{...contactInfoStyle, marginBottom: 0}}><strong>Phone:</strong> 76058 12086</p>
          </div>
        </div>

        {/* Section 16: Consent */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>16. Consent</h2>
          <p style={paragraphStyle}>
            By using the SRIMS Hostels app, you hereby consent to our privacy policy and agree to its terms.
          </p>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <p style={footerTextStyle}>© 2024 Learning Hub. All rights reserved.</p>
          <p style={{...footerTextStyle, marginBottom: 0}}>
            Developed for Shri Ram Institute of Management Studies (SRIMS)
          </p>
        </div>
      </div>

      {/* Back Button (Optional) */}
      {onNavigate && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '1.5rem',
          marginBottom: '1rem'
        }}>
          <button
            onClick={() => onNavigate('landing')}
            style={{
              background: 'linear-gradient(135deg, #0047AB 0%, #003087 100%)',
              color: 'white',
              padding: isMobile ? '10px 24px' : '12px 32px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 71, 171, 0.3)',
              fontSize: isMobile ? '14px' : '15px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(0, 71, 171, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 71, 171, 0.3)';
            }}
          >
            Back to Home
          </button>
        </div>
      )}
    </div>
  );
};

export default PrivacyPolicy;